import { useEffect, useRef, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import { useTranslation } from "react-i18next";

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";
const TURNSTILE_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const loadTurnstile = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Turnstile can only load in the browser."));
      return;
    }

    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener(
        "load",
        () => resolve(window.turnstile),
        { once: true }
      );
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = reject;
    document.body.appendChild(script);
  });

const TurnstileCaptcha = ({ error, onError, onTokenChange, siteKey }) => {
  const { i18n, t } = useTranslation();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [isLoading, setIsLoading] = useState(Boolean(siteKey));

  useEffect(() => {
    let cancelled = false;

    if (!siteKey) {
      setIsLoading(false);
      onTokenChange("");
      return undefined;
    }

    setIsLoading(true);
    onTokenChange("");

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;

        if (widgetIdRef.current) {
          turnstile.remove(widgetIdRef.current);
        }

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onTokenChange(token),
          "error-callback": () => {
            onTokenChange("");
            onError?.();
          },
          "expired-callback": () => onTokenChange(""),
          language: i18n.resolvedLanguage || i18n.language || "auto",
          theme: "light",
        });
      })
      .catch(() => {
        if (!cancelled) {
          onError?.();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;

      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [i18n.language, i18n.resolvedLanguage, onError, onTokenChange, siteKey]);

  if (!siteKey) {
    return null;
  }

  return (
    <Stack spacing={1.25}>
      <Stack alignItems="center" direction="row" spacing={1}>
        <SecurityIcon color="primary" fontSize="small" />
        <Typography
          color="text.primary"
          component="p"
          sx={{ fontWeight: 700 }}
          variant="body2"
        >
          {t("authRegister.captcha.title")}
        </Typography>
      </Stack>
      <Box
        ref={containerRef}
        sx={{
          minHeight: 65,
          width: "100%",
          "& iframe": {
            maxWidth: "100%",
          },
        }}
      />
      {isLoading && (
        <Typography color="text.secondary" variant="caption">
          {t("authRegister.captcha.loading")}
        </Typography>
      )}
      {error && (
        <Alert severity="error">{t("authRegister.captcha.error")}</Alert>
      )}
    </Stack>
  );
};

export default TurnstileCaptcha;
